
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { onAuthStateChanged, isSignInWithEmailLink, signInWithEmailLink, User } from 'firebase/auth';
import { auth, isFirebaseConfigured } from './src/firebaseConfig';
import WelcomeScreen from './components/WelcomeScreen';
import Quiz from './components/Quiz';
import LoadingScreen from './components/LoadingScreen';
import ResultsDashboard from './components/ResultsDashboard';
import PaymentConfirmationModal from './components/PaymentConfirmationModal';
import { UserData, SubmissionLog, ProgramStatus, ProfessionalSession, ClientProfile } from './types';
import { determineEligibility } from './services/eligibilityService';
import { getPublicIP, logSubmission, saveClientProfile, getClientsForUser, syncUserProfile, saveDraft } from './services/dbService'; 
import Header from './components/Header';
import StatsSection from './components/StatsSection';
import WhyChooseUs from './components/WhyChooseUs';
import HowItWorks from './components/HowItWorks';
import ReadyToGetStarted from './components/ReadyToGetStarted';
import ResourcesPage from './components/ResourcesPage';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import AccountCreationModal from './components/AccountCreationModal';
import LoginModal from './components/LoginModal';
import WhyForgivePathPage from './components/WhyForgivePathPage';
import WelcomeEmailModal from './components/WelcomeEmailModal';
import { sendSubmissionEmail, sendWelcomeEmail } from './services/emailService';
import LegalPage from './components/LegalPage';
import AdminDashboard from './components/AdminDashboard';
import UnifiedAIAssistant from './components/UnifiedAIAssistant';
import Footer from './components/Footer';
import { ADMIN_EMAILS, TESTER_EMAILS } from './config';

type AppState = 'welcome' | 'quiz' | 'loading' | 'results' | 'resources' | 'why-forgive-path' | 'legal' | 'admin';

const App: React.FC = () => {
  const [appState, setAppState] = useState<AppState>('welcome');
  const appStateRef = useRef<AppState>('welcome');
  
  useEffect(() => {
    appStateRef.current = appState;
  }, [appState]);

  const [userDatabase, setUserDatabase] = useState<Record<string, ProfessionalSession>>({});
  const [professionalSession, setProfessionalSession] = useState<ProfessionalSession | null>(null);
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);
  const [currentUserUid, setCurrentUserUid] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAccountCreationModal, setShowAccountCreationModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [tempFiles, setTempFiles] = useState<Record<string, Record<string, File>>>({});
  const [quizMode, setQuizMode] = useState<'new' | 'edit'>('new');
  const [submissionModalId, setSubmissionModalId] = useState<string | null>(null);
  const [paymentSuccessDetected, setPaymentSuccessDetected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [adSource, setAdSource] = useState<string | null>(AdSourceFromUrl());
  
  const pendingGuestProfile = useRef<ClientProfile | null>(null);

  // Load pending profile from localStorage on mount
  useEffect(() => {
    const saved = window.localStorage.getItem('pendingGuestProfile');
    if (saved) {
      try {
        const profile = JSON.parse(saved) as ClientProfile;
        pendingGuestProfile.current = profile;
        console.log("[AUTH] Restored pending guest profile from storage");
        
        // Optimistically set session if not already set by auth
        setProfessionalSession(prev => {
            if (prev) return prev;
            return {
                professionalEmail: '',
                clients: { [profile.clientId]: profile },
                activeClientId: profile.clientId
            };
        });
        
        // If we were in the middle of a magic link, we might want to be in results state
        const params = new URLSearchParams(window.location.search);
        if (params.get('email_link') === 'true' || isSignInWithEmailLink(auth, window.location.href)) {
            setAppState('results');
        }
      } catch (e) {
        console.error("Failed to parse saved guest profile", e);
      }
    }
  }, []);

  useEffect(() => { 
    // Immediate scroll
    window.scrollTo(0, 0); 
    // Delayed scroll to catch any late renders or height changes
    const timer = setTimeout(() => window.scrollTo(0, 0), 100);
    return () => clearTimeout(timer);
  }, [appState]);

  function AdSourceFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('utm_source') || params.get('ref') || null;
  }

  // --- HANDLE PASSWORDLESS EMAIL LINKS ---
  useEffect(() => {
      if (isFirebaseConfigured && auth && isSignInWithEmailLink(auth, window.location.href)) {
          let email = window.localStorage.getItem('emailForSignIn');
          if (!email) {
              email = window.prompt('Please provide your email for confirmation');
          }
          if (email) {
              setIsSyncing(true);
              signInWithEmailLink(auth, email, window.location.href)
                  .then(() => {
                      window.localStorage.removeItem('emailForSignIn');
                      window.history.replaceState({}, '', window.location.pathname);
                  })
                  .catch((error) => {
                      console.error("Magic Link Sync Error:", error);
                      alert("Invalid or expired sign-in link.");
                      setIsSyncing(false);
                      setAppState('welcome');
                  });
          }
      }
  }, []);

  // --- REAL-TIME DATABASE SYNC ---
  const syncFromFirestore = useCallback(async (uid: string, email: string) => {
    console.log(`[SYNC] Starting sync for ${email}`);
    setIsSyncing(true);
    try {
      const isNewUser = await syncUserProfile(uid, email);
      
      // If user just signed in and has a pending profile from a quiz, save it now
      if (pendingGuestProfile.current) {
          const profileToSave = { ...pendingGuestProfile.current, ownerId: uid };
          await saveClientProfile(uid, profileToSave);
          // Store a temporary copy to bridge the Firestore sync gap
          window.localStorage.setItem('pendingGuestProfile_lastSaved', JSON.stringify(profileToSave));
          pendingGuestProfile.current = null;
          window.localStorage.removeItem('pendingGuestProfile');
      }

      const clientsList = await getClientsForUser(uid);
      const clientsMap: Record<string, ClientProfile> = {};
      clientsList.forEach(c => clientsMap[c.clientId] = c);

      // If we just saved a pending guest profile, ensure it's in the map even if Firestore is slow
      const savedGuest = window.localStorage.getItem('pendingGuestProfile_lastSaved');
      if (savedGuest) {
          try {
              const p = JSON.parse(savedGuest) as ClientProfile;
              clientsMap[p.clientId] = { ...p, ownerId: uid };
              window.localStorage.removeItem('pendingGuestProfile_lastSaved');
          } catch (e) {}
      }

      const sortedClients = [...clientsList].sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      const activeId = sortedClients.length > 0 ? sortedClients[0].clientId : null;

      // Handle New User Welcome
      if (isNewUser) {
          console.log("%c[PROD] New user detected. Sending welcome email.", "color: green; font-weight: bold;");
          const welcomeProfile = pendingGuestProfile.current || (activeId ? clientsMap[activeId] : null);
          const profileToUse = welcomeProfile || { 
              clientId: 'unknown', ownerId: uid, userData: { 
                email, 
                name: email.split('@')[0], 
                employmentType: 'private', 
                yearsInPublicService: 0, 
                onIDR: false, 
                hasDisability: false,
                loanEstimatedBalance: 0,
                loanServicer: 'other',
                referralSource: 'other'
              }, eligiblePrograms: [], 
              paymentStatus: 'free', submissionLogs: [], createdAt: new Date().toISOString(), isTester: false
          };
          
          sendWelcomeEmail(email, profileToUse as ClientProfile).then(result => {
              if (result.success) {
                  console.log("%c[PROD] Welcome email sent successfully.", "color: green; font-weight: bold;");
              } else {
                  console.error("[PROD] Welcome email failed:", result.error);
              }
          });
          setShowWelcomeModal(true);
      }

      setProfessionalSession(prev => {
        const mergedClients = { ...clientsMap, ...(prev?.clients || {}) };
        return {
          professionalEmail: email,
          clients: mergedClients,
          activeClientId: prev?.activeClientId || activeId
        };
      });
      
      setIsTestMode(TESTER_EMAILS.includes(email));
      
      // Check for upgrade intent
      if (window.localStorage.getItem('upgradeIntent') === 'true') {
          window.localStorage.removeItem('upgradeIntent');
          setShowPaymentModal(true);
      }

      if (appStateRef.current === 'welcome' && !ADMIN_EMAILS.includes(email) && activeId) {
        setAppState('results');
      }
    } catch (error) {
      console.error("[SYNC] Firestore sync failed:", error);
    } finally {
      setIsSyncing(false);
    }
  }, []); // Removed appState dependency to prevent infinite loop

  useEffect(() => {
      console.log(`[CONFIG] Firebase Configured: ${isFirebaseConfigured}`);
      if (!isFirebaseConfigured || !auth) {
          setIsSyncing(false);
          return;
      }
      return onAuthStateChanged(auth, (user) => {
          console.log(`[AUTH] State changed: ${user?.email || 'No User'}`);
          if (user) {
              setCurrentUserEmail(user.email);
              setCurrentUserUid(user.uid);
              syncFromFirestore(user.uid, user.email || '');
          } else {
              setCurrentUserEmail(null);
              setCurrentUserUid(null);
              // Only clear session if it was a professional/logged-in session
              setProfessionalSession(prev => (prev?.professionalEmail ? null : prev));
              if (!isSignInWithEmailLink(auth, window.location.href)) {
                  setIsSyncing(false);
              }
          }
      });
  }, [syncFromFirestore]);

  useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('payment_success') === 'true') { 
        setPaymentSuccessDetected(true); 
        window.history.replaceState({}, '', window.location.pathname); 
      }
  }, []);

  useEffect(() => {
      if (paymentSuccessDetected && professionalSession && professionalSession.activeClientId && currentUserUid) {
         const activeId = professionalSession.activeClientId;
         const profile = professionalSession.clients[activeId];
         
         if (profile.paymentStatus !== 'paid') {
             const updatedProfile = { 
               ...profile, 
               paymentStatus: 'paid' as const, 
               transactionId: `txn_${new Date().getTime()}` 
             };
             
             saveClientProfile(currentUserUid, updatedProfile);
             setProfessionalSession(prev => prev ? {
               ...prev,
               clients: { ...prev.clients, [activeId]: updatedProfile }
             } : null);
             setShowPaymentModal(true);
         }
         setPaymentSuccessDetected(false);
      }
  }, [paymentSuccessDetected, professionalSession, currentUserUid]);

  const activeClient = useMemo(() => {
    if (!professionalSession) return null;
    if (professionalSession.activeClientId && professionalSession.clients[professionalSession.activeClientId]) {
        return professionalSession.clients[professionalSession.activeClientId];
    }
    // Fallback: pick the first client if we are in results state but activeId is lost
    const clientIds = Object.keys(professionalSession.clients);
    if (clientIds.length > 0) {
        return professionalSession.clients[clientIds[0]];
    }
    return null;
  }, [professionalSession]);

  useEffect(() => {
    if (appState === 'results' && !activeClient && professionalSession) {
        console.warn("[DEBUG] Results state active but no activeClient found. Session:", professionalSession);
    }
  }, [appState, activeClient, professionalSession]);

  const handleStartQuiz = useCallback((forceNew = false) => {
    if (forceNew || !professionalSession?.activeClientId) setQuizMode('new'); else setQuizMode('edit');
    setAppState('quiz');
  }, [professionalSession]);
  
  const handleSubmitQuiz = useCallback(async (data: UserData) => {
    setAppState('loading');
    await new Promise(r => setTimeout(r, 1500));
    
    const programs = determineEligibility(data);
    const clientId = quizMode === 'edit' && activeClient ? activeClient.clientId : `client_${Date.now()}`;
    
    const newProfile: ClientProfile = {
        clientId,
        ownerId: currentUserUid || 'anonymous',
        userData: { ...data, referralSource: adSource || data.referralSource },
        eligiblePrograms: programs,
        paymentStatus: activeClient?.paymentStatus || 'free',
        submissionLogs: activeClient?.submissionLogs || [],
        createdAt: activeClient?.createdAt || new Date().toISOString(),
        isTester: currentUserEmail ? TESTER_EMAILS.includes(currentUserEmail) : false
    };

    if (currentUserUid) {
        await saveClientProfile(currentUserUid, newProfile);
        setProfessionalSession(prev => {
            const updatedClients = { ...(prev?.clients || {}), [clientId]: newProfile };
            return {
                professionalEmail: currentUserEmail || '',
                clients: updatedClients,
                activeClientId: clientId
            };
        });
        setAppState('results');
        window.scrollTo(0, 0);
    } else {
        pendingGuestProfile.current = newProfile;
        window.localStorage.setItem('pendingGuestProfile', JSON.stringify(newProfile));
        setProfessionalSession({
            professionalEmail: '',
            clients: { [clientId]: newProfile },
            activeClientId: clientId
        });
        setAppState('results'); 
        window.scrollTo(0, 0);
    }
  }, [activeClient, quizMode, currentUserEmail, currentUserUid, adSource]);

  const handleLogout = useCallback(async () => {
      if (auth && isFirebaseConfigured) await auth.signOut();
      setAppState('welcome');
  }, []);

  const handleApplicationSubmit = useCallback(async (programIds: string[], data: Record<string, any>, documents: Record<string, File>) => {
    if (!professionalSession || !activeClient || !currentUserUid) return;
    
    const ipAddress = await getPublicIP();
    const newLogPrograms: { id: string; name: string; confirmationId?: string }[] = [];
    
    const updatedPrograms = activeClient.eligiblePrograms.map(program => {
        if (programIds.includes(program.id) && program.status === 'action-required') {
            const newStatus: ProgramStatus = program.submissionType === 'electronic' ? 'in-review' : 'submitted';
            const confirmationId = program.submissionType === 'electronic' ? `DOE-${program.id.toUpperCase()}-${Math.random().toString(36).substring(2, 10).toUpperCase()}` : undefined;
            newLogPrograms.push({ id: program.id, name: program.name, confirmationId });
            return { ...program, status: newStatus, confirmationId, lastUpdated: new Date().toISOString() };
        }
        return program;
    });

    const newLog: SubmissionLog = {
      id: `sub_${new Date().getTime()}`, 
      timestamp: new Date().toISOString(), 
      mode: isTestMode ? 'test' : 'live',
      submittedData: data, 
      submittedDocuments: Object.values(documents).map(f => f.name), 
      programs: newLogPrograms,
      metadata: { ipAddress: ipAddress, userAgent: navigator.userAgent, location: adSource || 'direct' }
    };

    if (Object.keys(documents).length > 0) setTempFiles(prev => ({...prev, [newLog.id]: documents}));

    const updatedProfile = { 
      ...activeClient, 
      submissionLogs: [newLog, ...activeClient.submissionLogs], 
      eligiblePrograms: updatedPrograms 
    };

    await saveClientProfile(currentUserUid, updatedProfile);
    await logSubmission(currentUserUid, newLog);

    setProfessionalSession(prev => prev ? {
        ...prev,
        clients: { ...prev.clients, [activeClient.clientId]: updatedProfile }
    } : null);

    setSubmissionModalId(newLog.id);
    if (activeClient?.userData.email) {
        console.log("%c[PROD] Attempting to send submission confirmation email...", "color: blue; font-weight: bold;");
        sendSubmissionEmail(activeClient.userData.email, newLog, documents).then(result => {
            if (result.success) {
                console.log("%c[PROD] Submission email sent successfully.", "color: green; font-weight: bold;");
            } else {
                console.error("[PROD] Submission email failed:", result.error);
                // Optionally we could notify the user here, but since it's an async background task, 
                // we'll mainly rely on logs for now to avoid disrupting the UI flow.
            }
        });
    }
  }, [isTestMode, professionalSession, activeClient, currentUserUid, adSource]);

  const updateProgramStatus = useCallback(async (pid: string, s: ProgramStatus) => {
    if(!professionalSession || !activeClient || !currentUserUid) return;
    const updatedPrograms = activeClient.eligiblePrograms.map(p => p.id === pid ? {...p, status: s} : p);
    const updatedProfile = { ...activeClient, eligiblePrograms: updatedPrograms };
    await saveClientProfile(currentUserUid, updatedProfile);
    setProfessionalSession(prev => prev ? {
      ...prev,
      clients: { ...prev.clients, [activeClient.clientId]: updatedProfile }
    } : null);
  }, [professionalSession, activeClient, currentUserUid]);

  const renderContent = () => {
    if (isSyncing) return <LoadingScreen />;
    switch (appState) {
      case 'quiz': return <Quiz onSubmit={handleSubmitQuiz} initialData={activeClient?.userData} />;
      case 'loading': return <LoadingScreen />;
      case 'results': return activeClient ? <ResultsDashboard clientProfile={activeClient} allClients={professionalSession?.clients || {}} onSwitchClient={(id) => setProfessionalSession(prev => prev ? {...prev, activeClientId: id} : null)} onAddNewClient={() => handleStartQuiz(true)} onUpgrade={() => { 
          if(currentUserUid) {
              setShowPaymentModal(true); 
          } else {
              window.localStorage.setItem('upgradeIntent', 'true');
              setShowAccountCreationModal(true); 
          }
      }} onApplicationSubmit={handleApplicationSubmit} isTestMode={isTestMode} onToggleTestMode={() => setIsTestMode(!isTestMode)} onUpdateProgramStatus={updateProgramStatus} tempFiles={tempFiles} onRecheckEligibility={() => handleStartQuiz(false)} onDeleteAccount={handleLogout} multiClientModeEnabled={!!professionalSession?.multiClientModeEnabled} onToggleMultiClientMode={() => setProfessionalSession(prev => prev ? {...prev, multiClientModeEnabled: !prev.multiClientModeEnabled} : null)} submissionModalId={submissionModalId} onConfirmationModalClose={() => setSubmissionModalId(null)} /> : <div className="text-center py-20"><button onClick={() => handleStartQuiz(true)} className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold">Create Your First Profile</button></div>;
      case 'resources': return <ResourcesPage userData={activeClient?.userData} />;
      case 'why-forgive-path': return <WhyForgivePathPage onStartQuiz={() => handleStartQuiz(false)} />;
      case 'legal': return <LegalPage />;
      case 'admin': return <AdminDashboard userDatabase={userDatabase} currentUserEmail={currentUserEmail} />;
      default: return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gray-50 text-gray-800 flex flex-col selection:bg-[#2ECC40] selection:text-white`}>
      <Header isLoggedIn={!!currentUserUid} professionalEmail={currentUserEmail || undefined} onNavigateHome={() => setAppState('welcome')} onNavigateToCheckEligibility={() => handleStartQuiz(false)} onNavigateToDashboard={() => { if(currentUserEmail && ADMIN_EMAILS.includes(currentUserEmail)) setAppState('admin'); else if(professionalSession?.activeClientId) setAppState('results'); else setAppState('quiz'); }} onNavigateToResources={() => setAppState('resources')} onNavigateToWhyForgivePath={() => setAppState('why-forgive-path')} onLoginClick={() => setShowLoginModal(true)} onCreateAccountClick={() => handleStartQuiz(false)} onLogoutClick={handleLogout} />
      <div className="flex-grow relative z-0 pt-32 overflow-x-hidden flex flex-col">
        {appState === 'welcome' && !isSyncing ? (
          <>
            <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 bg-white"><WelcomeScreen onStartQuiz={() => handleStartQuiz(false)} onLoginClick={() => setShowLoginModal(true)} /></main>
            <div className="bg-white"><StatsSection /></div>
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-white"><WhyChooseUs /></div>
            <HowItWorks /><AnalyticsDashboard /><div className="bg-white"><ReadyToGetStarted onStartQuiz={() => handleStartQuiz(false)} /></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
              <UnifiedAIAssistant variant="inline" onSubmitQuiz={()=>{}} />
            </div>
          </>
        ) : (
          <>
            <main className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-grow">{renderContent()}</main>
            {appState !== 'quiz' && appState !== 'admin' && appState !== 'results' && appState !== 'loading' && (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                <UnifiedAIAssistant 
                  variant="inline" 
                  onSubmitQuiz={()=>{}}
                  userEmail={currentUserEmail}
                  userId={currentUserUid || undefined}
                  paymentStatus={activeClient?.paymentStatus}
                  hasSubmitted={activeClient?.submissionLogs.length ? activeClient.submissionLogs.length > 0 : false}
                />
              </div>
            )}
          </>
        )}
        <Footer onNavigate={(page) => setAppState(page)} />
      </div>
      {showPaymentModal && <PaymentConfirmationModal onClose={() => setShowPaymentModal(false)} userEmail={currentUserEmail} isTestMode={isTestMode} isAlreadyPaid={activeClient?.paymentStatus === 'paid'} onPaymentSuccess={() => setPaymentSuccessDetected(true)} />}
      {showAccountCreationModal && <AccountCreationModal onClose={() => setShowAccountCreationModal(false)} onCreateAccountSuccess={(user) => { setShowAccountCreationModal(false); setAppState('results'); }} tempSession={professionalSession} />}
      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} onLoginSuccess={(user) => { setShowLoginModal(false); setAppState('results'); }} />}
      {showWelcomeModal && <WelcomeEmailModal onClose={() => setShowWelcomeModal(false)} onContinue={() => { setShowWelcomeModal(false); setShowPaymentModal(true); }} userEmail={currentUserEmail} />}
    </div>
  );
};
export default App;
